import { Inject, Injectable } from '@angular/core';
import { Deferred } from 'src/app/common/deferred/deferred';
import { EnvironmentService } from 'src/app/shared/services/environment/environment.service';

import { TrackerService } from '@services/tracker.service';
import makeDebug from '../../../../makeDebug';
import { PipelineToken } from '../contracts/pipeline-token';
import { IPipelineContext } from '../contracts/start/pipeline-context';
import { IPipelineElement } from '../contracts/start/pipeline-element';
import { IPipelineRegistry } from '../contracts/start/pipeline-registry';

const debug = makeDebug('sync:pipeline-registry');

@Injectable({ providedIn: 'root' })
export class PipelineRegistry implements IPipelineRegistry {
  private _context: IPipelineContext;
  private _ready = new Deferred<void>();
  public get ready(): Promise<void> {
    return this._ready.promise;
  }

  constructor(
    @Inject(PipelineToken) private _pipelineElements: IPipelineElement[],
    private _environmentService: EnvironmentService,
    private _trackerService: TrackerService
  ) {
    this.init().catch(error => window.logger.error('[PipelineRegistry] init failed', error));
  }

  private async init() {
    window.logger.addBreadcrumb({ message: '[PipelineRegistry] init waiting for environment' });
    await this._environmentService.ready;

    this._context = {
      id: `${Date.now()}`,
      registry: this,
      params: { isLogin: false, uri: this._environmentService.alberta_baseurl },
      executeOnlyOnce: new Map<string, boolean>(),
    };
    const firstElement = this._pipelineElements.find(pipelineElement => pipelineElement.parent === '');
    if (!firstElement) {
      return this._ready.reject(new Error('No root of pipe element defined.'));
    }

    const sortedPipelineElements = [firstElement];

    this.sortByParent(sortedPipelineElements);

    this._pipelineElements.length = 0;
    this._pipelineElements.push(...sortedPipelineElements);

    this._ready.resolve();
    window.logger.addBreadcrumb({
      message: `[PipelineRegistry] init done, ${this._pipelineElements.length} elements sorted`,
    });
  }

  public async start(): Promise<void> {
    await this._ready.promise;
    window.logger.addBreadcrumb({ message: '[PipelineRegistry] start' });
    this._trackerService.debugPipelineStart();
    for (const pipelineElement of this._pipelineElements) {
      debug('executing pipeline element', pipelineElement.endpoint, this._context);
      window.logger.addBreadcrumb({ message: `[PipelineRegistry] RUN ${pipelineElement.endpoint}` });
      this._trackerService.debugPipelineElementStart(pipelineElement.endpoint);
      const result = await pipelineElement.execute(this._context);
      window.logger.addBreadcrumb({ message: `[PipelineRegistry] DONE ${pipelineElement.endpoint}` });
      this._trackerService.debugPipelineElementDone(pipelineElement.endpoint, result);
    }
    window.logger.addBreadcrumb({ message: '[PipelineRegistry] pipeline complete' });
    this._trackerService.debugPipelineDone();
  }

  sortByParent(sortedPipelineElements: IPipelineElement[]) {
    const childElement = this._pipelineElements.find(
      pipelineElement => sortedPipelineElements[sortedPipelineElements.length - 1].endpoint === pipelineElement.parent
    );
    if (!childElement) {
      return;
    }

    sortedPipelineElements.push(childElement);
    this.sortByParent(sortedPipelineElements);
  }
}
